'use client';

import { ReactNode, useEffect } from 'react';
import { Modals } from '@/components/modals';
import { SWRConfig } from 'swr';
import { FetchError, logToSentryOnFetchError } from '@/utils';
import { DefaultPageData } from '@/utils/getPageDefaultData';
import { useSetDefaultLocale } from '@/hooks/general/useLocale/useLocale';
import { useSurveyLinkIfShortCookieExists } from '@/hooks/forms/affiliates/useSurveyLink/useSurveyLinkIfShortCookieExists';
import { LoginPopup } from '@/components/blocks/login/LoginPopup/LoginPopup';
import { TopLoader } from '@/components/layout/TopLoader/TopLoader';
import { useLogoutOnSessionEnd } from '@/hooks/general/useLogoutOnSessionEnd/useLogoutOnSessionEnd';
import { useFetchSummaryIfAuthorised } from '@/hooks/general/useFetchSummaryIfAuthorised/useFetchSummaryIfAuthorised';
import { useSubpanelPhrasesUpdate } from '@/hooks/general/useSubpanelPhrasesUpdate/useSubpanelPhrasesUpdate';
import { useConfigUpdate } from '@/hooks/general/useConfigUpdate';
import { useStoreRedirectJwt } from '@/hooks/general/useStoreRedirectJwt';
import { useRedirects } from '@/hooks/general/useRedirects/useRedirects';
import { useNextRouterForLinksInPhrases } from '@/hooks/general/useNextRouterForLinksInPhrases/useNextRouterForLinksInPhrases';
import { useUpdatePhrases } from '@/hooks/general/useUpdatePhrases';
import { useHasHydrated } from '@/hooks/general/useHasHydrated';
import { useForbiddenPaths } from '@/hooks/general/useForbiddenPaths/useForbiddenPaths';
import { useHomeQueryParams } from '@/hooks/general/useHomeQueryParams/useHomeQueryParams';
import { ThemeVariables } from '@/themes/mpanels/variables';
import { ThemeRegistry } from '@/context/ThemeRegistry/ThemeRegistry';
import { Theme } from '@mui/material/styles';
import { ContentPreloader } from '@/components/layout/ContentPreloader/ContentPreloader';

interface AppProps {
    children: ReactNode;
    defaultData: DefaultPageData;
    theme: Theme;
    variables: ThemeVariables;
}

export default function App({ children, defaultData, theme, variables }: AppProps) {
    useConfigUpdate(defaultData.config);

    useStoreRedirectJwt();
    useLogoutOnSessionEnd();
    useRedirects();
    useNextRouterForLinksInPhrases();
    useSetDefaultLocale();
    useUpdatePhrases(defaultData?.phrases ?? {});
    useFetchSummaryIfAuthorised();
    useSubpanelPhrasesUpdate();

    const { showPopup, startLink, phraseId, closePopup } = useSurveyLinkIfShortCookieExists();
    const hasHydrated = useHasHydrated();
    const { isNotRestrictedPath, isContentUnavailableToShow } = useForbiddenPaths();
    const { checkForHowHeardIdAndUpdate } = useHomeQueryParams();

    if (hasHydrated) checkForHowHeardIdAndUpdate();

    const renderComponent = () => {
        if (!hasHydrated || (!isNotRestrictedPath && isContentUnavailableToShow)) {
            return <ContentPreloader />;
        }
        return <>{children}</>;
    };

    useEffect(() => {
        window.recaptchaOptions = {
            useRecaptchaNet: true,
        };
    }, []);

    return (
        <ThemeRegistry theme={theme} variables={variables}>
            <SWRConfig
                value={{
                    onError: (err: FetchError) => logToSentryOnFetchError(err),
                }}
            >
                <TopLoader>
                    {renderComponent()}
                    <LoginPopup
                        showPopup={showPopup}
                        startLink={startLink}
                        phraseId={phraseId}
                        closePopup={closePopup}
                    />
                    <Modals />
                </TopLoader>
            </SWRConfig>
        </ThemeRegistry>
    );
}
